import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const MAX_VISIBLE_BADGES = 4;

export function BadgeRow({ badges }: { badges: Array<{ key: string; node: React.ReactNode }> }) {
  const [expanded, setExpanded] = useState(false);
  const overflow = badges.length - MAX_VISIBLE_BADGES;
  const visible = expanded ? badges : badges.slice(0, MAX_VISIBLE_BADGES);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map(b => <span key={b.key}>{b.node}</span>)}
      {overflow > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="inline-flex items-center gap-0.5 rounded-full border border-border/60 bg-muted/30 px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted/60"
        >
          {expanded ? (
            <>show less <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>+{overflow} more <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      ) : null}
    </div>
  );
}

export const formatRelativeOrShort = (value?: string | null): string => {
  if (!value) return '—';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;

  const diffMs = Date.now() - parsed;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  const date = new Date(parsed);
  const nowYear = new Date().getFullYear();
  if (date.getFullYear() === nowYear) {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
